// @ts-nocheck
import * as React from "react";
import { Sidebar, Topbar, AppFooter, Ic, AvatarImg, conflictMessage, apiErrorMessage } from "./common";

// 매니저용 데스크톱 /cod — 본인 매칭 인박스 + 결정 흐름.
//
// 권역 운영자용 cod.tsx와는 별도 컴포넌트로 분리: 운영자 화면은 권역 큐·후보 풀·
// 가중치 시뮬레이션·재생성 등 운영 도구가 메인이지만, 매니저는 "내가 받은 제안을
// 어떻게 처리할지"만 필요. 모바일 MgrMatch와 거의 같은 데이터·결정 흐름이지만
// 데스크톱 chrome(사이드바·토프바·세로 카드 리스트)로 재포팅.

const RISK_LABEL = { critical: "위험", warning: "주의", stable: "안정" };
const RISK_COLOR = { critical: "rose", warning: "amber", stable: "green" };

// 모바일 MgrMatch와 동일한 결정 카피. 한 곳만 손볼 일이 생기면 common.tsx로
// 끌어올릴 가치가 있지만, 현재 2개 화면뿐이라 인라인 유지 (간단함이 더 큼).
const DECISION_CONFIRM = {
  accepted: {
    title: "수락하시겠어요?",
    body: "지금 어르신이 담당으로 배정되고 본인 일정에 자동 추가됩니다. 다른 후보 매칭은 일괄 보류로 전환됩니다.",
    cta: "수락 · 일정 추가",
    ctaClass: "btn btn-primary btn-md",
  },
  rejected: {
    title: "거절하시겠어요?",
    body: "거절 후에는 같은 어르신의 동일 제안이 다시 노출되지 않습니다.",
    cta: "거절",
    ctaClass: "btn btn-md",
    ctaStyle: { background: "var(--rose-500)", color: "#fff" },
  },
  hold: {
    title: "보류하시겠어요?",
    body: "보류 처리하면 이번 큐에서 제외되고, 다음 매칭 검토 시 다시 검토 후보로 올라옵니다.",
    cta: "보류",
    ctaClass: "btn btn-ghost btn-md",
  },
};

async function jsonFetch(url, opts = {}) {
  const res = await fetch(url, { credentials: "same-origin", ...opts });
  let body = null;
  try { body = await res.json(); } catch { /* non-JSON */ }
  if (!res.ok) {
    const err = new Error(body?.message || body?.error || `HTTP ${res.status}`);
    err.status = res.status; err.body = body;
    err.code = body?.error;
    throw err;
  }
  return body;
}

function humanError(e) {
  if (!e) return "처리에 실패했습니다.";
  if (e.status === 401) return "로그인이 필요합니다.";
  if (e.status === 403) return "권한이 없습니다.";
  if (e.status === 404) return "해당 항목을 찾을 수 없습니다.";
  if (e.status === 409) return conflictMessage(e.body);
  return apiErrorMessage(e);
}

function CODManager() {
  const [inbox, setInbox] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [feedback, setFeedback] = React.useState(null);
  // pending = { matchId, decision } — 확인 시트 열림 상태.
  const [pending, setPending] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const loadInbox = React.useCallback(async () => {
    setErr(null);
    try {
      const j = await jsonFetch("/api/v1/mobile/manager/match_inbox");
      setInbox(j.matches || []);
    } catch (e) { setErr(humanError(e)); }
  }, []);

  React.useEffect(() => { loadInbox(); }, [loadInbox]);

  // feedback 자동 사라짐.
  React.useEffect(() => {
    if (!feedback) return;
    const id = window.setTimeout(() => setFeedback(null), 2500);
    return () => window.clearTimeout(id);
  }, [feedback]);

  // 시트 Esc 닫기 (작업 중엔 무시).
  React.useEffect(() => {
    if (!pending) return;
    const onKey = (e) => { if (e.key === "Escape" && !busy) setPending(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [pending, busy]);

  const decide = React.useCallback(async (matchId, decision) => {
    if (!matchId || busy) return;
    setBusy(true); setErr(null);
    try {
      await jsonFetch(`/api/v1/matches/${matchId}/decide`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ decision }),
      });
      const label = decision === "accepted" ? "수락" : decision === "hold" ? "보류" : "거절";
      const target = inbox?.find(x => x.match.id === matchId);
      setFeedback({ kind: "success", text: `${target?.elderly?.name || "제안"} ${label} 처리됨` });
      // 처리된 카드는 로컬 인박스에서 제거. 빈 큐는 아래 렌더에서 empty 화면.
      setInbox(arr => (arr ? arr.filter(it => it.match.id !== matchId) : arr));
    } catch (e) {
      setFeedback({ kind: "error", text: humanError(e) });
    } finally { setBusy(false); }
  }, [busy, inbox]);

  const confirmPending = React.useCallback(async () => {
    if (!pending || busy) return;
    await decide(pending.matchId, pending.decision);
    setPending(null);
  }, [pending, busy, decide]);

  const count = inbox ? inbox.length : 0;
  const pendingItem = pending && inbox?.find(x => x.match.id === pending.matchId);

  return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        <Sidebar active="cod"/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar title="내 매칭 제안" sub={inbox === null ? "" : `${count}건 검토 대기`}/>
          <div className="main">

            {feedback && (
              <div role="status" aria-live="polite"
                style={{marginBottom:12, padding:'10px 14px',
                        background: feedback.kind === "success" ? "var(--green-50)" : "var(--rose-50)",
                        color:      feedback.kind === "success" ? "var(--green-700)" : "var(--rose-500)",
                        borderRadius:10, fontSize:13, fontWeight:600}}>
                {feedback.kind === "success" ? "✓ " : "⚠ "}{feedback.text}
              </div>
            )}

            {err && (
              <div className="card" style={{padding:14, marginBottom:12, color:"var(--rose-500)", background:"var(--rose-50)"}}>
                {err}
              </div>
            )}

            {!err && inbox === null && <div className="muted" style={{padding:30}}>불러오는 중…</div>}

            {!err && inbox && inbox.length === 0 && (
              <div className="card card-pad" style={{textAlign:'center', padding:'50px 24px'}}>
                <div style={{fontSize:48, marginBottom:14}}>✓</div>
                <div style={{fontSize:16, fontWeight:700, marginBottom:6}}>모든 제안을 처리했습니다</div>
                <div className="muted" style={{fontSize:12.5}}>새 매칭이 들어오면 알림으로 알려드릴게요.</div>
              </div>
            )}

            {!err && inbox && inbox.length > 0 && (
              <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(420px, 1fr))', gap:14}}>
                {inbox.map((item) => (
                  <MatchCard key={item.match.id} item={item} busy={busy}
                    onAction={(decision) => setPending({ matchId: item.match.id, decision })}/>
                ))}
              </div>
            )}

          </div>
          <AppFooter/>
        </div>
      </div>

      {pending && pendingItem && (() => {
        const c = DECISION_CONFIRM[pending.decision];
        const e = pendingItem.elderly;
        const close = () => { if (!busy) setPending(null); };
        return (
          <div role="dialog" aria-modal="true" aria-label={c.title}
            onClick={close}
            style={{position:'fixed', inset:0, zIndex:50, background:'rgba(20,16,12,0.45)',
                    display:'grid', placeItems:'center'}}>
            <div onClick={(ev) => ev.stopPropagation()}
              style={{background:'#fff', borderRadius:14, padding:'22px 24px',
                      width:420, maxWidth:'calc(100vw - 40px)', boxShadow:'0 16px 40px rgba(0,0,0,0.18)'}}>
              <div style={{fontSize:17, fontWeight:800, marginBottom:8}}>{c.title}</div>
              {e?.name && (
                <div className="muted" style={{fontSize:12.5, marginBottom:10}}>
                  <b style={{color:'var(--ink-1)'}}>{e.name} 어르신</b>
                  {e.age != null && ` · ${e.age}세`}
                  {e.risk_level && (
                    <span className={"badge badge-" + RISK_COLOR[e.risk_level]}
                      style={{fontSize:9.5, marginLeft:6}}>{RISK_LABEL[e.risk_level]}</span>
                  )}
                </div>
              )}
              <div style={{fontSize:13, lineHeight:1.6, color:'var(--ink-2)', marginBottom:18}}>{c.body}</div>
              <div style={{display:'flex', gap:8, justifyContent:'flex-end'}}>
                <button type="button" className="btn btn-ghost btn-md"
                  onClick={close} disabled={busy}>취소</button>
                <button type="button" className={c.ctaClass}
                  style={{...(c.ctaStyle || {})}}
                  onClick={confirmPending} disabled={busy}>
                  {busy ? "처리 중…" : c.cta}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function MatchCard({ item, busy, onAction }) {
  const m = item.match;
  const e = item.elderly;
  const dims = [
    { l:'거리', v:`${m.distance_km}km`, s:m.score_distance },
    { l:'자격', v:'매치',                s:m.score_qualification },
    { l:'가용', v:'여유',                s:m.score_availability },
    { l:'부하', v:'적정',                s:m.score_load },
    { l:'이력', v:'양호',                s:m.score_history },
    { l:'평가', v:'★',                   s:m.score_rating },
  ];
  return (
    <div className="card" style={{padding:18, display:'flex', flexDirection:'column', gap:14,
                                  border:'1.5px solid var(--orange-500)', position:'relative'}}>
      <div style={{position:'absolute', top:-10, right:18, padding:'4px 12px',
                   background:'var(--orange-500)', color:'#fff', borderRadius:99,
                   fontSize:10, fontWeight:800}}>
        {m.rank}순위 추천
      </div>

      <div style={{display:'flex', alignItems:'center', gap:12}}>
        <AvatarImg className="avatar lg" url={e?.avatar_url} name={e?.name}/>
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontSize:16, fontWeight:800}}>{e?.name} 어르신 · {e?.age}세</div>
          <div className="muted" style={{fontSize:11, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
            {e?.address} {e?.risk_level && `· ${RISK_LABEL[e.risk_level]}`}
          </div>
        </div>
        <div style={{textAlign:'right'}}>
          <div className="mono" style={{fontSize:32, fontWeight:800, color:'var(--orange-600)', lineHeight:1}}>{m.score}</div>
          <div className="muted" style={{fontSize:9, fontWeight:700, letterSpacing:'0.1em'}}>MATCH</div>
        </div>
      </div>

      <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10}}>
        {dims.map((d, i) => (
          <div key={i}>
            <div style={{display:'flex', justifyContent:'space-between', fontSize:10, fontWeight:700, marginBottom:4}}>
              <span style={{color:'var(--ink-2)'}}>{d.l}</span>
              <span className="mono" style={{color: d.s >= 90 ? 'var(--green-600)' : 'var(--orange-600)'}}>{d.s}</span>
            </div>
            <div className="bar">
              <span style={{width:`${d.s}%`, background: d.s >= 90 ? 'var(--green-500)' : 'var(--orange-500)'}}/>
            </div>
          </div>
        ))}
      </div>

      {m.reasoning && (
        <div style={{padding:'10px 12px', background:'var(--bg-sub)', borderRadius:10}}>
          <div className="muted" style={{fontSize:11, lineHeight:1.5}}>{m.reasoning}</div>
        </div>
      )}

      <div style={{display:'flex', gap:8}}>
        <button type="button" className="btn btn-ghost btn-md" style={{flex:1}}
          onClick={() => onAction("hold")} disabled={busy}>보류</button>
        <button type="button" className="btn btn-ghost btn-md"
          onClick={() => onAction("rejected")} disabled={busy}
          style={{color:'var(--rose-500)', flex:'0 0 auto', padding:'0 16px'}}>
          {Ic.close}
        </button>
        <button type="button" className="btn btn-primary btn-md" style={{flex:2}}
          onClick={() => onAction("accepted")} disabled={busy}>수락 · 일정 추가 →</button>
      </div>
    </div>
  );
}

export { CODManager };
